// ---------------------------------------------------------------
// SOP engine — the Florida Beauty broker-update cadence AND the AI
// dispatcher's driver conversation, as autonomous per-load procedures.
//
// Each load status carries:
//   messages[] — broker updates on the reply-all thread. trigger:
//                'auto'   → system knows it (GPS/status) → autopilot sends
//                'manual' → needs info the driver provides → fires once logged
//   asks[]     — proactive TEXTS the AI sends the driver to gather info
//                (door #, BOL/seal/pallet/temp photos, POD, OS&D, trailer).
//   Driver answers are logged on load.driverInfo and flow back into the
//   broker messages (real door #, set temp, clean POD / OS&D, trailer empty).
// ---------------------------------------------------------------

const MONEY_CAP = 300;

// ---- dispatcher profile (per analyst; signs broker emails + driver texts) ----
const PROFILE_KEY = 'dd-dispatcher-profile';
const DEFAULT_PROFILE = { name: '', title: 'Inbound Dispatcher', company: 'Florida Beauty Express', phone: '305-503-1200', ext: '', cell: '', email: '', initials: '', signOff: 'Thanks,' };
export function getProfile() {
  try { return { ...DEFAULT_PROFILE, ...(JSON.parse(localStorage.getItem(PROFILE_KEY)) || {}) }; }
  catch { return DEFAULT_PROFILE; }
}
export function saveProfile(p) { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); }
export const getInitials = () => getProfile().initials || '';
export const setInitials = (v) => saveProfile({ ...getProfile(), initials: (v || '').trim() });

export function getSignature() {
  const p = getProfile();
  if (!p.name) return '';
  const lines = [p.signOff || 'Thanks,', '', p.name];
  if (p.title) lines.push(p.title);
  if (p.company) lines.push(p.company);
  const ph = [p.phone ? `Ph: ${p.phone}` : '', p.ext ? `ext ${p.ext}` : ''].filter(Boolean).join(' ');
  if (ph) lines.push(ph);
  if (p.cell) lines.push(`Cell: ${p.cell}`);
  if (p.email) lines.push(`Email: ${p.email}`);
  return '\n\n' + lines.join('\n');
}

const CHAIN_BANNER = '***PLEASE REPLY ALL FOR UPDATES***\nPLEASE ADD YOUR TRACKING TEAM TO THIS EMAIL CHAIN FOR UPDATES\nPLEASE ONLY USE THIS EMAIL CHAIN FOR UPDATES!!';

// ---- stop helpers (multi-stop aware) ----
const pickups = (load) => (load.stops || []).filter((s) => s.type === 'pickup');
const deliveries = (load) => (load.stops || []).filter((s) => s.type === 'delivery');
const firstPickup = (load) => pickups(load)[0] || load.shipper || {};
const lastDelivery = (load) => { const d = deliveries(load); return d[d.length - 1] || load.consignee || {}; };
const isMultiStop = (load) => (pickups(load).length + deliveries(load).length) > 2;
const puRef = (load) => firstPickup(load).refNumber || '[PU#]';

const sign = () => { const i = getInitials(); return i ? ' ' + i : ''; }; // driver texts sign with initials
const multiLine = (load) => (isMultiStop(load) ? '\nMulti-stop — specify the order (1st, 2nd, 3rd).' : '');
const dt = (s) => (s ? new Date(s).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '[time]');

const fmtDate = (s) => { if (!s) return '[date]'; const d = new Date(s); return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`; };
const fmtTime = (s) => { if (!s) return '[time]'; const d = new Date(s); let h = d.getHours(); const m = d.getMinutes(); const ap = h < 12 ? 'AM' : 'PM'; h = ((h + 11) % 12) + 1; return m ? `${h}:${String(m).padStart(2, '0')}${ap}` : `${h}${ap}`; };

// The driver dispatch text sent the moment a load is assigned. Matches the FB
// format exactly; multi-stop aware (numbers each PICK UP / DELIVERY in order).
export function driverDispatchText(load) {
  const p = getProfile();
  const div = '---------------------------------------------';
  const block = (s, label) => [
    `${label}:`,
    (s.name || '').toUpperCase(),
    s.address || '',
    `${(s.city || '').toUpperCase()}, ${s.state || ''} ${s.zip || ''}`.replace(/\s+/g, ' ').trim().replace(/^,\s*/, ''),
    `DATE: ${fmtDate(s.early)}  TIME: ${fmtTime(s.early)}`,
  ].filter((x) => x && x.trim()).join('\n');

  const pus = pickups(load), dels = deliveries(load);
  const lines = [div,
    `LOAD#: ${load.billNumber || ''}`,
    `TRIP#: ${load.tripNumber || ''}`,
    `TEMP: ${(load.temp || 'DRY').toUpperCase()}`,
    `PU#: ${firstPickup(load).refNumber || ''}`,
    div];
  pus.forEach((s, i) => { lines.push(block(s, pus.length > 1 ? `PICK UP #${i + 1}` : 'PICK UP')); lines.push(div); });
  lines.push(`               ${(load.release && load.release.trim()) ? load.release.toUpperCase() : 'NO RELEASE'}`);
  lines.push(div);
  dels.forEach((s, i) => { lines.push(block(s, dels.length > 1 ? `DELIVERY #${i + 1}` : 'DELIVERY')); lines.push(div); });
  lines.push(`PLEASE CONFIRM WITH "OK" ONCE YOU\nRECEIVE THIS MESSAGE, THANKS${p.name ? ', ' + p.name.split(' ')[0] + '.' : '.'}`);
  return lines.join('\n');
}

function arrivalTime(load, type) {
  const stops = ((load.geo || {}).stops) || {};
  const hit = Object.values(stops).filter((s) => s.type === type && s.arrival).sort((a, b) => new Date(a.arrival) - new Date(b.arrival)).pop();
  return hit ? dt(hit.arrival) : '[time]';
}
function detentionBlock(load, type) {
  const stops = ((load.geo || {}).stops) || {};
  const hit = Object.values(stops).find((s) => s.type === type && (s.detentionHrs || 0) > 0.02 && s.arrival);
  if (!hit) return '';
  return `\n\nDetention applies (stayed past free time):\nIn Time: ${dt(hit.arrival)}\nOut Time: ${dt(hit.departure)}`;
}

// ---- broker-thread subject: BROKER LOAD 68007667 PA TO FL TR 2211 TL 7262 ----
export function brokerSubject(load, ctx = {}) {
  const broker = (load.caller && load.caller.name ? load.caller.name.split(' ')[0] : '').toUpperCase();
  const num = load.brokerLoadNumber || load.billNumber || '';
  const origSt = firstPickup(load).state || '';
  const destSt = lastDelivery(load).state || '';
  const truck = load.truckNumber || ctx.truckName || '';
  const trailer = load.trailerNumber || '';
  const parts = [];
  if (broker) parts.push(broker);
  parts.push('LOAD', num);
  if (origSt || destSt) parts.push(`${origSt} TO ${destSt}`);
  if (truck) parts.push(`TR ${truck}`);
  if (trailer) parts.push(`TL ${trailer}`);
  return parts.filter(Boolean).join(' ').trim();
}

// info we need the driver to hand us at each stage (drives the asks + status chips)
export const DRIVER_FIELDS = {
  pickupDoor: 'Shipper door #', setTemp: 'Reefer set point',
  bolReceived: 'BOL photos', sealReceived: 'Seal photo', palletsReceived: 'Pallets + load-locks', tempReceived: 'Temp set-point photo',
  deliveryDoor: 'Receiver door #', podReceived: 'POD photos', lumper: 'Lumper', osd: 'OS&D noted', trailerEmpty: 'Trailer empty',
};

// which fields each stage is waiting on (for "waiting on driver" chips)
export function stageNeeds(load) {
  const di = load.driverInfo || {};
  switch (load.status) {
    case 'At Pickup': return di.pickupDoor ? [] : ['pickupDoor'];
    case 'Loaded': return ['bolReceived', 'sealReceived', 'palletsReceived', 'tempReceived'].filter((k) => !di[k]);
    case 'At Delivery': return di.deliveryDoor ? [] : ['deliveryDoor'];
    case 'Delivered': return ['podReceived'].filter((k) => !di[k]);
    default: return [];
  }
}

// ---- stage definitions, keyed by load status ----
export function sopStage(load, ctx = {}) {
  const s = load.status;
  const di = load.driverInfo || {};
  const fp = firstPickup(load), ld = lastDelivery(load);
  const puCity = [fp.city, fp.state].filter(Boolean).join(', ') || '[shipper]';
  const conCity = [ld.city, ld.state].filter(Boolean).join(', ') || '[receiver]';
  const conCityState = `${ld.city || '[city]'}, ${ld.state || '[ST]'}`;
  const subject = brokerSubject(load, ctx);
  const sg = getSignature();
  const truck = load.truckNumber || ctx.truckName || '[TR#]';
  const trailer = load.trailerNumber || '[TL#]';
  const driver = ctx.driverName || fp.contact || '[driver]';
  const driverPhone = ctx.driverPhone || '[driver phone]';
  const num = load.brokerLoadNumber || load.billNumber || '';
  const miles = ctx.miles != null ? ctx.miles : '[X]';

  const upd = (line) => `Good day,\n\n${line}${sg}`;
  // trigger: 'auto' = fire on geofence gate · 'reply' = fire when the driver's
  // reply fills `requires` fields · 'manual' = dispatcher fires it.
  const M = (key, label, trigger, body, requires) => ({ key, label, trigger, subject, body, requires: requires || null });
  const A = (key, text, needs) => ({ key, text: `Hi ${driver}, ${text}`.replace(/\s+$/, '') + sign(), needs });

  // driver-provided values, filled into the broker messages
  const puDoor = di.pickupDoor || '___';
  const delDoor = di.deliveryDoor || '___';
  const tempSet = di.setTemp ? `: ${di.setTemp}` : '';
  const lumperLine = di.lumper ? di.lumper : '[none / $amount — receipt below]';
  const podLine = di.osd ? `OS&D noted: ${di.osd}` : (di.podReceived ? 'clean' : 'clean / OS&D: [none noted]');
  const trailerLine = di.trailerEmpty === 'empty' ? 'completely empty' : di.trailerEmpty === 'remaining' ? 'product remaining' : '[empty / product remaining]';

  const STAGES = {
    Available: {
      code: '—', title: 'New load — set up', messages: [], asks: [],
      tools: ['Rate con from Gustavo', 'Dispatch Summary Card template'],
      checklist: [
        'Print rate confirmation + "Template Dispatch Summary Card", staple, fill all required info',
        'Create the load and assign all required resources; verify each is active and available',
        'Enter the broker Load #, Truck # and Trailer # (for the email subject)',
      ],
    },

    Dispatched: {
      code: 'DESP', title: 'Dispatched — headed to shipper',
      messages: [
        M('driver-info', 'Driver information (starts the thread)', 'auto',
          `EMPTY NOW\nETA to shipper ${ctx.eta || dt(load.pickup && load.pickup.early)}\n\nTR ${truck}\nTL ${trailer}\n${driver} - ${driverPhone}\n\n${CHAIN_BANNER}${sg}`),
        M('day-of-reminder', 'Day-of-pickup reminder — still good', 'auto',
          upd(`Still good on load ${num}. Delivering to ${conCityState}.\nDriver is wrapping up the previous delivery (precons) now — we'll send ETA to the shipper once empty.`)),
        M('empty-precons-eta', 'Empty at precons + ETA to shipper', 'manual',
          upd(`Empty at the previous delivery (precons).\nETA to the shipper: [time].`)),
      ],
      asks: [
        { key: 'dispatch-info', text: driverDispatchText(load), needs: 'confirm', label: 'Dispatch load info (sent on assignment)' },
        A('empty-eta', `are you empty and rolling to the shipper in ${puCity}? What's your ETA? Pick-up # ${puRef(load)}.`, 'eta'),
      ],
      tools: ['Samsara + Google Maps: track truck/trailer, calc miles & ETA', 'Call in / Dispatch: set status DESP', 'Confirm ETA to shipper by call and/or text'],
      checklist: ['Send driver-info email (starts the thread)', 'Day-of reminder + delivery city/state', 'ETA to shipper once empty at precons'],
    },

    'At Pickup': {
      code: 'ARRSHIP', title: 'At shipper',
      messages: [
        M('arrived-shipper', 'Arrived at shipper', 'auto',
          upd(`Driver arrived at the shipper at ${arrivalTime(load, 'pickup')}, waiting to be loaded.`)),
        M('got-door-shipper', 'Got a door — auto-sends when driver replies with door #', 'reply',
          upd(`Driver has door # ${puDoor} assigned and is getting loaded.`), ['pickupDoor']),
      ],
      asks: [
        A('pickup-door', `you're at the shipper — what door number did they give you? Let me know when they start loading.`, 'pickupDoor'),
      ],
      tools: ['Samsara + Google Maps: track truck/trailer', 'Call in / Dispatch: set status ARRSHIP', `EFS / Relay: money code (≤ $${MONEY_CAP}) if lumper`, 'Send lumper receipt to Billing'],
      checklist: ['Send "arrived at shipper"', 'Ask driver for + send door number', 'Handle lumper + money code if any'],
    },

    Loaded: {
      code: 'DEPSHIP', title: 'Loaded & ready to roll',
      messages: [
        M('loaded', 'Loaded & ready to roll — auto-sends when driver sends the photos', 'reply',
          upd(`Loaded and ready to roll.${multiLine(load)}\n\nPlease see photos below:\n- BOL — every page, all four corners\n- Seal\n- Pallets inside the trailer with the load locks (before closing the doors)\n- Temperature set point${tempSet}${detentionBlock(load, 'pickup')}`), ['bolReceived', 'sealReceived', 'palletsReceived', 'tempReceived']),
      ],
      asks: [
        A('loaded-photos', `before you close the doors, send me: 1) BOL — every page, all four corners, 2) the seal, 3) the pallets with the load locks inside the trailer, 4) the temperature set point. What temp are you set at?`, 'photos'),
      ],
      tools: ['Samsara: verify hours at the facility', 'ThermoKing: confirm reefer set point', 'Call in / Dispatch: set status DEPSHIP', 'Collect + attach all four photos before sending'],
      checklist: ['Ask driver for BOL photos (all pages, 4 corners)', 'Ask for seal photo', 'Ask for pallets + load-locks photo before doors close', 'Ask for temperature set-point photo', 'If stayed > 3 hrs: document detention'],
    },

    'In Transit': {
      code: 'En route (SPTDL + Yard ZIP if released)', title: 'In transit — periodic updates',
      messages: [
        M('transit-update', 'Transit update (2–3×/day: location, miles, temp)', 'auto',
          upd(`Current location: [location]. About ${miles} miles from the receiver. Reefer temp: ${di.setTemp || '[temp]'}°F.\nLooking good for delivery as scheduled.`)),
      ],
      asks: [
        A('transit-temp', `quick update please — send me your current location and a photo of the reefer temperature.`, 'temp'),
      ],
      tools: ['Samsara + Google Maps: live location + miles/ETA', 'ThermoKing / Google Voice: request reefer temp photo', 'If released to yard: set status SPTDL with Yard ZIP + Release Template + Empty List'],
      checklist: ['Text driver for location + temp (2–3×/day)', 'Send location + miles + temp to broker', 'If yard release: Release Template + Empty List'],
    },

    'At Delivery': {
      code: 'ARRCONS', title: 'At consignee',
      messages: [
        M('arrived-delivery', 'Arrived at receiver (with time)', 'auto',
          upd(`Driver arrived at the receiver at ${arrivalTime(load, 'delivery')}.${multiLine(load)}`)),
        M('got-door-delivery', 'Got a door — auto-sends when driver replies with door #', 'reply',
          upd(`Driver has door # ${delDoor} assigned and is getting unloaded.`), ['deliveryDoor']),
      ],
      asks: [
        A('delivery-door', `you're at the receiver — what door number did they assign? Let me know when they start unloading.`, 'deliveryDoor'),
      ],
      tools: ['Samsara + Google Maps: track truck/trailer', 'Call in / Dispatch: set status ARRCONS', `EFS / Relay: money code (≤ $${MONEY_CAP}) if lumper`],
      checklist: ['Send "arrived at receiver" with arrival time', 'Ask driver for + send door number', 'Handle lumper + money code if any'],
    },

    Delivered: {
      code: 'DEPCON', title: 'Delivered — completion report',
      messages: [
        M('completed', 'Load completed — auto-sends when driver sends the POD', 'reply',
          upd(`Load completed.${multiLine(load)}\n- Lumper: ${lumperLine}\n- POD: ${podLine}\n- Trailer checked: ${trailerLine}\n\nPOD photos below — every page, all four corners.${detentionBlock(load, 'delivery')}\n\n${di.lumper ? 'There was a lumper — please send a revised rate con.' : '[If there was a lumper: please send a revised rate con.]'}`), ['podReceived']),
      ],
      asks: [
        A('pod', `please send the POD — every page, all four corners. Was there a lumper (send the receipt)? Any shortage, damage, or overage marked on the bills? Is the trailer completely empty?`, 'pod'),
      ],
      tools: ['Samsara: verify hours at the facility', 'Call in / Dispatch: set status DEPCON', 'Send lumper receipt to Billing', 'If lumper: request a revised rate con from the broker'],
      checklist: ['Ask driver for POD (all pages, 4 corners)', 'Confirm lumper + receipt', 'Confirm clean POD or note OS&D on the bills', 'Confirm trailer empty / product remaining', 'If lumper: ask broker for revised rate con', 'If stayed > 3 hrs: document detention'],
    },

    Complete: {
      code: 'DEPCON → Billing', title: 'Complete — ready to bill',
      messages: [M('final', 'Final confirmation', 'manual', upd('Load completed, clean bill. POD attached. Thank you.'))],
      asks: [],
      tools: ['Accounting / Billing: attach POD + lumper receipts', 'Confirm detention charges before invoicing', 'If lumper: ensure revised rate con received'],
      checklist: ['POD received and filed', 'Revised rate con received (if lumper)', 'Detention documented + billed per stop', 'Ready to invoice'],
    },

    Invoiced: { code: 'Billed', title: 'Invoiced', messages: [], asks: [], tools: [], checklist: ['Invoice sent', 'Payment tracked'] },
  };

  const stage = STAGES[s] || STAGES.Available;
  return { status: s, subject, broker: (stage.messages && stage.messages[0]) || null, ...stage, puCity, conCity, needs: stageNeeds(load) };
}

// ---------------------------------------------------------------
// Priority 1 / Critical detection.
// ---------------------------------------------------------------
export const CRITICAL_KINDS = ['Accident / Incident', 'Breakdown', 'Reefer temp', 'Rejection', 'Transfer load', 'Rescue load', 'Detention risk'];
export function loadCritical(load, freeHours = 3) {
  const flags = [];
  const stops = ((load.geo || {}).stops) || {};
  const now = Date.now();
  Object.values(stops).forEach((s) => {
    if (s.arrival && !s.departure) {
      const base = s.appt ? new Date(s.appt).getTime() : new Date(s.arrival).getTime();
      const cutoff = base + freeHours * 3600000;
      if (now > cutoff) {
        const hrs = ((now - cutoff) / 3600000).toFixed(1);
        flags.push({ kind: 'Detention risk', detail: `${s.type === 'pickup' ? 'Pickup' : 'Delivery'} #${s.seq}${s.city ? ' — ' + s.city : ''} past free time by ${hrs} hr`, at: s.arrival });
      }
    }
  });
  if (load.critical && load.critical.active) flags.push({ kind: load.critical.kind || 'Rescue load', detail: load.critical.note || 'Flagged critical', at: load.critical.at });
  return flags;
}

// At-a-glance flags for a dispatched / in-transit load: late + temp + detention
// + any manual critical. Used for the quick status badge on the board.
export function loadGlanceFlags(load) {
  const flags = [...loadCritical(load)];
  const now = Date.now();
  const fp = firstPickup(load), ld = lastDelivery(load);
  const pickupDone = ['Loaded', 'In Transit', 'At Delivery', 'Delivered', 'Complete', 'Invoiced'].includes(load.status);
  const done = ['Delivered', 'Complete', 'Invoiced'].includes(load.status);
  if (!pickupDone && fp.early && now > new Date(fp.early).getTime()) {
    flags.push({ kind: 'Late', detail: `Past pickup appointment (${dt(fp.early)})` });
  }
  if (!done && ld.early && now > new Date(ld.early).getTime()) {
    flags.push({ kind: 'Late', detail: `Past delivery appointment (${dt(ld.early)})` });
  }
  return flags;
}

// Pre-billing checklist — everything that must be in before a load can bill.
export function billingChecklist(load) {
  const d = load.documents || {};
  const di = load.driverInfo || {};
  const lumperStr = String(di.lumper || '').trim();
  const hadLumper = (!!lumperStr && !/^(no|none|n\/a|0|\$?0(\.00)?)$/i.test(lumperStr))
    || (load.charges || []).some((c) => /lump/i.test((c.code || '') + (c.description || '')));

  const items = [
    { key: 'delivered', label: 'Load delivered', ok: ['Delivered', 'Complete', 'Invoiced'].includes(load.status) },
    { key: 'pod', label: 'POD received', ok: !!d.pod || !!di.podReceived },
    { key: 'cleanPod', label: di.osd ? `OS&D noted (${di.osd})` : 'Clean POD / OS&D confirmed', ok: !!di.podReceived || !!d.pod },
    { key: 'bol', label: 'BOL on file', ok: !!d.bol },
    { key: 'rateCon', label: 'Rate confirmation on file', ok: !!d.rateCon },
  ];
  if (hadLumper) {
    items.push({ key: 'lumperReceipt', label: 'Lumper receipt', ok: !!d.lumperReceipt });
    items.push({ key: 'revisedRateCon', label: 'Revised rate con from broker (lumper)', ok: !!d.revisedRateCon });
  }
  const missing = items.filter((i) => !i.ok);
  return { items, missing, ready: missing.length === 0, hadLumper };
}

// ---------------------------------------------------------------
// Load issues / delays — the driver texts in a problem, the load flags for a
// dispatcher, and the AI composes an update to the broker + the company.
// ---------------------------------------------------------------
export const DELAY_REASONS = [
  'Breakdown / mechanical', 'Flat tire', 'Accident / incident', 'Reefer malfunction',
  'Weather', 'Traffic / road closure', 'Driver out of hours (HOS)', 'Driver illness',
  'Shipper delay', 'Receiver / appointment delay', 'Load rejected', 'Running late', 'Other',
];

// map a reason to a Priority-1 kind so it shows the right badge
export function reasonKind(reason = '') {
  const r = reason.toLowerCase();
  if (/break|mechanical|flat|tire|engine/.test(r)) return 'Breakdown';
  if (/accident|incident|crash/.test(r)) return 'Accident / Incident';
  if (/reefer|temp/.test(r)) return 'Reefer temp';
  if (/reject/.test(r)) return 'Rejection';
  return 'Rescue load';
}

const cityLane = (load) => `${(load.shipper && load.shipper.city) || '—'} → ${(load.consignee && load.consignee.city) || '—'}`;

// Broker-facing issue update (stays on the load's reply-all thread)
export function issueBrokerEmail(load, ctx = {}) {
  const sg = getSignature();
  const iss = load.issue || {};
  const loc = iss.location || ctx.location || '';
  const note = iss.note ? `\n- Details: ${iss.note}` : '';
  const body = `Good day,\n\nWe want to make you aware of an issue on this load:\n- Issue: ${iss.reason || 'Delay'}${loc ? `\n- Location: ${loc}` : ''}${note}\n\nOur team is on it and working to protect the load. We'll follow up with a revised ETA and updates as soon as we have them.${sg}`;
  return { subject: brokerSubject(load, ctx), body };
}

// Internal notice to the company's email contacts
export function issueCompanyEmail(load, ctx = {}) {
  const iss = load.issue || {};
  const loc = iss.location || ctx.location || '';
  const subject = `LOAD ISSUE — ${load.billNumber || ''} · ${iss.reason || 'Delay'}`.trim();
  const body = `Load ${load.billNumber || ''} (${cityLane(load)}) has an issue that needs attention.\n\n- Issue: ${iss.reason || 'Delay'}${loc ? `\n- Location: ${loc}` : ''}${iss.note ? `\n- Details: ${iss.note}` : ''}\n- Reported by: ${iss.reportedBy === 'driver' ? 'Driver' : 'Dispatch'}\n- Current status: ${load.status}\n- Truck: ${ctx.truckName || load.truckNumber || '—'} · Driver: ${ctx.driverName || '—'}\n\nDispatch is handling. Broker has been notified.`;
  return { subject, body };
}

export { MONEY_CAP };
